// src/contexts/TemplateContext.tsx
// Template caching context - uses localStorage for persistent instant access

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import { settingsApi } from "../services/api";

const STORAGE_KEY = "coderom_template_cache";
const CACHE_VERSION = "v1"; // Increment to invalidate old cache
const CACHE_MAX_AGE = 24 * 60 * 60 * 1000; // 24 hours

interface TemplateCache {
  [themeId: string]: {
    html: string;
    loadedAt: number;
  };
}

interface StoredCache {
  version: string;
  templates: TemplateCache;
}

interface TemplateContextType {
  getTemplate: (themeId: string) => string | null;
  loadTemplate: (themeId: string) => Promise<string>;
  preloadTemplates: (themeIds: string[]) => void;
  isLoading: (themeId: string) => boolean;
  cacheReady: boolean;
}

const TemplateContext = createContext<TemplateContextType | null>(null);

// Available themes to preload
const DEFAULT_THEMES = ["darkBlue", "redBlack", "greenNature", "modernWhite", "classic"];

// Load cache from localStorage
const loadFromStorage = (): TemplateCache => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return {};

    const parsed: StoredCache = JSON.parse(stored);

    // Check version
    if (parsed.version !== CACHE_VERSION) {
      console.log("[TemplateCache] Cache version mismatch, clearing...");
      localStorage.removeItem(STORAGE_KEY);
      return {};
    }

    // Filter out expired entries
    const now = Date.now();
    const valid: TemplateCache = {};
    for (const [key, value] of Object.entries(parsed.templates)) {
      if (now - value.loadedAt < CACHE_MAX_AGE) {
        valid[key] = value;
      }
    }

    console.log(`[TemplateCache] Loaded ${Object.keys(valid).length} templates from localStorage`);
    return valid;
  } catch (e) {
    console.error("[TemplateCache] Error loading from localStorage:", e);
    return {};
  }
};

// Save cache to localStorage
const saveToStorage = (cache: TemplateCache) => {
  try {
    const data: StoredCache = {
      version: CACHE_VERSION,
      templates: cache
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (e) {
    console.error("[TemplateCache] Error saving to localStorage:", e);
  }
};

export function TemplateProvider({ children }: { children: React.ReactNode }) {
  // Initialize from localStorage immediately
  const [cache, setCache] = useState<TemplateCache>(() => loadFromStorage());
  const [loading, setLoading] = useState<Set<string>>(new Set());
  const [cacheReady, setCacheReady] = useState(false);
  const loadingPromises = useRef<Map<string, Promise<string>>>(new Map());

  // Extract body content from full HTML
  const extractBody = (html: string): string => {
    const match = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
    return match ? match[1] : html;
  };

  // Get cached template (returns null if not cached)
  const getTemplate = useCallback((themeId: string): string | null => {
    return cache[themeId]?.html || null;
  }, [cache]);

  // Check if a template is currently loading
  const isLoading = useCallback((themeId: string): boolean => {
    return loading.has(themeId);
  }, [loading]);

  // Load a template (fetches if not cached)
  const loadTemplate = useCallback(async (themeId: string): Promise<string> => {
    // Return from cache if available
    if (cache[themeId]) {
      console.log(`[TemplateCache] Instant load from cache: ${themeId}`);
      return cache[themeId].html;
    }

    // Return existing promise if already loading
    const existingPromise = loadingPromises.current.get(themeId);
    if (existingPromise) {
      console.log(`[TemplateCache] Waiting for existing load: ${themeId}`);
      return existingPromise;
    }

    // Create new loading promise
    const loadPromise = (async () => {
      setLoading(prev => new Set(prev).add(themeId));
      console.log(`[TemplateCache] Fetching from API: ${themeId}`);

      try {
        const result = await settingsApi.getTemplateHtml(themeId);
        if (!result.success || !result.html) {
          throw new Error(result.error || "Template load failed");
        }

        const html = extractBody(result.html);

        // Cache the template (state + localStorage)
        setCache(prev => {
          const newCache = {
            ...prev,
            [themeId]: { html, loadedAt: Date.now() }
          };
          saveToStorage(newCache);
          return newCache;
        });

        console.log(`[TemplateCache] Cached to localStorage: ${themeId}`);
        return html;
      } finally {
        setLoading(prev => {
          const next = new Set(prev);
          next.delete(themeId);
          return next;
        });
        loadingPromises.current.delete(themeId);
      }
    })();

    loadingPromises.current.set(themeId, loadPromise);
    return loadPromise;
  }, [cache]);

  // Preload multiple templates in background
  // Using refs to avoid dependency issues and infinite loops
  const preloadTemplates = useCallback((themeIds: string[]) => {
    themeIds.forEach(themeId => {
      // Check if already cached or loading using refs
      const isCached = !!cache[themeId];
      const isLoading = loadingPromises.current.has(themeId);

      if (!isCached && !isLoading) {
        loadTemplate(themeId).catch(() => {}); // Ignore errors for preload
      }
    });
  }, [loadTemplate]); // Only depend on loadTemplate, not cache

  // Track if initial preload has been done
  const initialPreloadDone = useRef(false);

  // Preload default templates AND CKEditor on mount (once only)
  useEffect(() => {
    if (initialPreloadDone.current) return;
    initialPreloadDone.current = true;

    const cachedCount = Object.keys(cache).length;
    console.log(`[TemplateCache] Init: ${cachedCount} templates in localStorage cache`);

    // Mark cache as ready
    setCacheReady(true);

    // Preload missing templates in background
    const missing = DEFAULT_THEMES.filter(t => !cache[t]);
    if (missing.length > 0) {
      console.log(`[TemplateCache] Preloading missing: ${missing.join(", ")}`);
      // Direct fetch without going through preloadTemplates to avoid deps issues
      missing.forEach(themeId => {
        if (!loadingPromises.current.has(themeId)) {
          loadTemplate(themeId).catch(() => {});
        }
      });
    } else {
      console.log("[TemplateCache] All default templates already cached!");
    }

    // 🚀 PRELOAD CKEditor script in background for faster Edit page loading
    const CKEDITOR_URL = "https://cdn.ckeditor.com/4.22.1/full-all/ckeditor.js";
    if (!(window as any).CKEDITOR) {
      console.log("[TemplateCache] Preloading CKEditor script...");
      const script = document.createElement('script');
      script.src = CKEDITOR_URL;
      script.async = true;
      script.onload = () => console.log("[TemplateCache] CKEditor preloaded ✓");
      document.head.appendChild(script);
    }
  }, []); // Run once on mount - using ref to ensure single execution

  return (
    <TemplateContext.Provider value={{
      getTemplate,
      loadTemplate,
      preloadTemplates,
      isLoading,
      cacheReady
    }}>
      {children}
    </TemplateContext.Provider>
  );
}

export function useTemplates() {
  const context = useContext(TemplateContext);
  if (!context) {
    throw new Error("useTemplates must be used within a TemplateProvider");
  }
  return context;
}
